import { RankingEngine, rankingEngine } from './RankingEngine'
import type {
  ComparisonAlert,
  ComparisonFilters,
  ComparisonProductInput,
  ComparisonSortBy,
  ComparisonSummary,
  ProductComparisonGroup,
  RankedOffer,
  RankingWeights,
} from '../types'

/**
 * ComparisonEngine — group, dedupe, compare and rank supplier offers.
 */
export class ComparisonEngine {
  private readonly ranking: RankingEngine

  constructor(ranking: RankingEngine = rankingEngine) {
    this.ranking = ranking
  }

  setWeights(weights: Partial<RankingWeights>) {
    return this.ranking.setWeights(weights)
  }

  getWeights() {
    return this.ranking.getWeights()
  }

  compare(
    products: ComparisonProductInput[],
    filters: ComparisonFilters = {},
    sortBy: ComparisonSortBy = 'score_desc',
  ): { groups: ProductComparisonGroup[]; flat: RankedOffer[]; summary: ComparisonSummary } {
    const filtered = this.applyFilters(products, filters)
    const grouped = this.groupProducts(filtered)
    const groups = grouped.map((group) => this.rankGroup(group))
    const flat = this.sortOffers(
      groups.flatMap((g) => g.offers),
      sortBy,
    )

    return {
      groups,
      flat,
      summary: this.buildSummary(groups),
    }
  }

  groupKey(product: ComparisonProductInput): string {
    if (product.ean?.trim()) return `ean:${product.ean.trim().toLowerCase()}`
    const brand = product.brand.trim().toLowerCase()
    const model = product.model.trim().toLowerCase()
    const sku = product.sku.trim().toLowerCase()
    if (brand && model) return `bm:${brand}|${model}`
    if (sku) return `sku:${sku}`
    return `desc:${product.description.trim().toLowerCase().slice(0, 80)}`
  }

  private applyFilters(
    products: ComparisonProductInput[],
    filters: ComparisonFilters,
  ): ComparisonProductInput[] {
    return products.filter((p) => {
      if (filters.suppliers?.length && !filters.suppliers.includes(p.supplier)) return false
      if (filters.brands?.length && !filters.brands.includes(p.brand)) return false
      if (filters.categories?.length && !filters.categories.includes(p.category ?? '')) return false
      if (filters.minPrice != null && p.pixPrice < filters.minPrice) return false
      if (filters.maxPrice != null && p.pixPrice > filters.maxPrice) return false
      if (filters.inStockOnly && p.stock <= 0) return false
      if (filters.maxDeliveryDays != null && p.estimatedDelivery > filters.maxDeliveryDays) {
        return false
      }
      if (filters.query) {
        const q = filters.query.toLowerCase()
        const hay = `${p.description} ${p.brand} ${p.model} ${p.sku} ${p.supplier}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }

  private groupProducts(products: ComparisonProductInput[]): ComparisonProductInput[][] {
    const map = new Map<string, ComparisonProductInput[]>()
    for (const product of products) {
      const key = this.groupKey(product)
      const list = map.get(key) ?? []
      // Eliminate exact supplier+sku duplicates keeping freshest/cheapest
      const existingIndex = list.findIndex(
        (p) => p.supplier === product.supplier && p.sku === product.sku,
      )
      if (existingIndex >= 0) {
        const current = list[existingIndex]!
        if (product.pixPrice < current.pixPrice) list[existingIndex] = product
      } else {
        list.push(product)
      }
      map.set(key, list)
    }
    return [...map.values()].filter((g) => g.length > 0)
  }

  private rankGroup(offers: ComparisonProductInput[]): ProductComparisonGroup {
    const scores = this.ranking.scoreGroup(offers)
    const scoreMap = new Map(scores.map((s) => [s.id, s]))

    const minPrice = Math.min(...offers.map((o) => o.pixPrice))
    const maxStock = Math.max(...offers.map((o) => o.stock))
    const minDelivery = Math.min(...offers.map((o) => o.estimatedDelivery))
    const maxScore = Math.max(...scores.map((s) => s.score), 0)

    const ranked: RankedOffer[] = offers.map((offer) => {
      const scored = scoreMap.get(offer.id)!
      const alerts = this.detectAlerts(offer, minPrice)
      return {
        ...offer,
        groupKey: this.groupKey(offer),
        score: scored.score,
        scoreBreakdown: {
          price: scored.breakdown.price,
          stock: scored.breakdown.stock,
          delivery: scored.breakdown.delivery,
          freight: scored.breakdown.freight,
          reliability: scored.breakdown.reliability,
        },
        flags: {
          bestPrice: offer.pixPrice === minPrice,
          bestStock: offer.stock === maxStock && offer.stock > 0,
          fastestDelivery: offer.estimatedDelivery === minDelivery,
          bestScore: scored.score === maxScore,
        },
        alerts,
      }
    })

    ranked.sort((a, b) => b.score - a.score || a.pixPrice - b.pixPrice)
    const recommended = ranked[0] ?? null
    const prices = ranked.map((o) => o.pixPrice)
    const maxPrice = Math.max(...prices)
    const economy = recommended ? round2(maxPrice - recommended.pixPrice) : 0
    const priceDiff = recommended && prices.length > 1 ? round2(maxPrice - minPrice) : 0

    const sample = offers[0]!
    return {
      groupKey: this.groupKey(sample),
      label: sample.description,
      brand: sample.brand,
      model: sample.model,
      sku: sample.sku,
      offers: ranked,
      recommended,
      economy,
      priceDiff,
    }
  }

  private detectAlerts(offer: ComparisonProductInput, groupMinPrice: number): ComparisonAlert[] {
    const alerts: ComparisonAlert[] = []
    if (offer.stock <= 0) {
      alerts.push({
        code: 'OUT_OF_STOCK',
        severity: 'danger',
        message: 'Produto sem estoque',
      })
    }
    if (offer.estimatedDelivery >= 15) {
      alerts.push({
        code: 'LONG_DELIVERY',
        severity: 'warning',
        message: 'Prazo elevado',
      })
    }
    if (groupMinPrice > 0 && offer.pixPrice >= groupMinPrice * 1.25) {
      alerts.push({
        code: 'PRICE_TOO_HIGH',
        severity: 'warning',
        message: 'Preço muito acima',
      })
    }
    return alerts
  }

  private sortOffers(offers: RankedOffer[], sortBy: ComparisonSortBy): RankedOffer[] {
    const copy = [...offers]
    switch (sortBy) {
      case 'price_asc':
        return copy.sort((a, b) => a.pixPrice - b.pixPrice)
      case 'delivery_asc':
        return copy.sort((a, b) => a.estimatedDelivery - b.estimatedDelivery)
      case 'stock_desc':
        return copy.sort((a, b) => b.stock - a.stock)
      case 'freight_asc':
        return copy.sort((a, b) => (a.freight ?? 0) - (b.freight ?? 0))
      case 'score_desc':
      default:
        return copy.sort((a, b) => b.score - a.score || a.pixPrice - b.pixPrice)
    }
  }

  private buildSummary(groups: ProductComparisonGroup[]): ComparisonSummary {
    const flat = groups.flatMap((g) => g.offers)
    const recommended = groups
      .map((g) => g.recommended)
      .filter(Boolean)
      .sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0))[0]

    return {
      totalOffers: flat.length,
      groups: groups.length,
      recommendedSupplier: recommended?.supplier ?? null,
      economy: round2(groups.reduce((s, g) => s + g.economy, 0)),
      priceDiff: round2(groups.reduce((s, g) => s + g.priceDiff, 0)),
      bestPrice: flat.length ? Math.min(...flat.map((o) => o.pixPrice)) : null,
      bestScore: flat.length ? Math.max(...flat.map((o) => o.score)) : null,
    }
  }
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const comparisonEngine = new ComparisonEngine()
